export interface Award {
  id: string;
  title: string;
  year: number;
  organization: string;
  description: string;
  category?: string;
  link?: string;
  image?: string;
}

export interface Patent {
  id: string;
  title: string;
  patentNumber: string;
  year: number;
  inventors: string[];
  assignee: string;
  description: string;
  status: "Pending" | "Granted" | "Expired";
  link?: string;
}

export const awards: Award[] = [
  {
    id: "streaming-innovation-2021",
    title: "Best New Streaming Innovation Award",
    year: 2021,
    organization: "Streaming Media Readers' Choice Awards",
    description:
      "Awarded for the FaRes-ML approach: Fast Multi-Resolution and Multi-Rate Encoding for HTTP Adaptive Streaming Using Machine Learning",
    category: "Innovation",
    link: "https://www.streamingmedia.com/Articles/Editorial/Featured-Articles/The-2021-Streaming-Media-Readers-Choice-Award-Winners-149990.aspx",
  },
  {
    id: "doctoral-symposium-2021",
    title: "Best Doctoral Paper Award",
    year: 2021,
    organization:
      "ACM Multimedia Systems Conference (MMSys) Doctoral Symposium",
    description:
      'Best paper award for doctoral research on "Machine Learning Based Video Coding Enhancements for HTTP Adaptive Streaming"',
    category: "Academic Excellence",
    link: "https://2021.acmmmsys.org/index.php",
  },
  {
    id: "bandwidth-estimation-2024",
    title: "Runner-up in Bandwidth Estimation Challenge",
    year: 2024,
    organization: "ACM Multimedia Systems Conference (MMSys)",
    description:
      "Second place in the Second Bandwidth Estimation Challenge with the FARC approach: Fast Actor and Not-So-Furious Critic for offline reinforcement learning",
    category: "Competition",
    link: "https://www.microsoft.com/en-us/research/academic-program/bandwidth-estimation-challenge/results/",
  },
];

export const patents: Patent[] = [
  {
    id: "patent-fast-multi-rate-encoding-for-adaptive-http-streaming",
    title: "Fast multi-rate encoding for adaptive HTTP streaming",
    patentNumber: "US11546401B2",
    year: 2023,
    inventors: ["Hadi Amirpour", "Ekrem Çetinkaya", "Christian Timmerer"],
    assignee: "Bitmovin Inc.",
    description:
      "According to embodiments of the disclosure, information of higher and lower quality encoded video segments is used to limit Rate-Distortion Optimization (RDO) for each Coding Unit Tree (CTU). A method first encodes the highest bit-rate segment and consequently uses it to encode the lowest bit-rate video segment. Block structure and selected reference frame of both highest and lowest bit-rate video segments are used to predict and shorten RDO process for each CTU in middle bit-rates. The method delays just one frame using parallel processing. This approach provides time-complexity reduction compared to the reference software for middle bit-rates while degradation is negligible",
    status: "Granted",
    link: "https://patents.google.com/patent/US11546401B2/en",
  },
  {
    id: "patent-fast-multi-rate-encoding-for-adaptive-http-streaming",
    title:
      "Fast multi-rate encoding for adaptive streaming using machine learning",
    patentNumber: "US11936864B2",
    year: 2024,
    inventors: ["Hadi Amirpour", "Ekrem Çetinkaya", "Christian Timmerer"],
    assignee: "Bitmovin Inc.",
    description:
      "According to embodiments of the disclosure, fast multi-rate encoding may be performed using machine learning by encoding a lowest quality representation to determine encoding parameters, processing raw data of the video using a neural network to obtain an intermediate output comprising encoding features, augmenting the intermediate output with additional encoding features to form a final tensor, and processing the final tensor with another neural network to obtain a classification output comprising a split or not split decision for an image data block. The classification output may be used to encode a highest quality representation, and then other representations of the video.",
    status: "Granted",
    link: "https://patents.google.com/patent/US11936864B2/en",
  },
  {
    id: "patent-adaptive-bitrate-algorithm-deployed-at-edge-nodes",
    title: "Adaptive bitrate algorithm deployed at edge nodes ",
    patentNumber: "US11968245B2",
    year: 2024,
    inventors: [
      "Jesús Aguilar-Armijo",
      "Ekrem Çetinkaya",
      "Hermann Hellwagner",
      "Christian Timmerer",
    ],
    assignee: "Bitmovin Inc.",
    description:
      "The technology described herein relates to implementing an adaptive bitrate (ABR) algorithm at edge nodes. A method for implementing an ABR algorithm at an edge node may include receiving at the edge node a request for a video segment from a client according to the client's ABR algorithm, the request indicating a quality. A weighted sum score for each of a set of qualities may be computed based on a quality score and a fairness score using the ABR algorithm at the edge node, the qualities including at least the requested quality and another quality. A modified request may be generated in response to the weighted sum score for the other quality being better than the weighted sum score for the requested quality. The modified request may be sent to a server. The video segment in the other quality may be received from the server and provided to a client.",
    status: "Granted",
    link: "https://patents.google.com/patent/US11968245B2/en",
  },
  {
    id: "patent-device-adaptive-super-resolution-based-approach-to-adaptive-streaming",
    title:
      "Device-Adaptive Super-Resolution Based Approach to Adaptive Streaming",
    patentNumber: "US20240420283A1",
    year: 2024,
    inventors: [
      "Minh Nguyen",
      "Ekrem Çetinkaya",
      "Hermann Hellwagner",
      "Christian Timmerer",
    ],
    assignee: "Bitmovin Inc.",
    description:
      "A mobile device capable of device-adaptive super-resolution based adaptive streaming may include a super-resolution-based adaptive bitrate (SR-based ABR) application configured to receive an input and determine a quality of a representation of a video to request based on the input, the SR-based ABR application being able to compute a cost of requesting the quality of representation based on a cost function. The mobile device may apply an SR network to the representation of the video to upscale the representation of the video to a desired resolution. The mobile device also may include a display configured to play back a segment of the video. A method for SR-based adaptive streaming may include receiving an input, determining whether to request a lower resolution representation of a video segment based on the input, and applying an SR network to the lower resolution representation to upscale it to a desired resolution.",
    status: "Pending",
    link: "https://patentimages.storage.googleapis.com/78/35/de/46eadc878a91b8/US20240420283A1.pdf",
  },
  {
    id: "patent-lightweight-dense-residual-network-for-video-super-resolution-on-mobile-devices",
    title:
      "Lightweight Dense Residual Network for Video Super-Resolution on Mobile Devices ",
    patentNumber: "US20230419447A1",
    year: 2023,
    inventors: ["Ekrem Çetinkaya", "Minh Nguyen", "Christian Timmerer"],
    assignee: "Bitmovin Inc.",
    description:
      "The technology described herein relates to a lightweight dense residual network for video super-resolution on mobile devices. A method for implementing a lightweight dense residual network to achieve super-resolution performance may include generating feature maps using a network based on an input of frames at a lower resolution, the network comprised of DenseRes blocks and an additional convolution operation, each DenseRes block comprising multiple layers of convolution operations and rectified linear activation function (ReLU) operations and a 1×1 convolution operation. Said feature maps are upsampled by a pixel shuffle layer in the network and the frames are output at a higher resolution, the higher resolution relative to the lower resolution by an upscaling factor",
    status: "Pending",
    link: "https://patents.google.com/patent/US20230419447A1/en",
  },
];
